import { useEffect, useState } from 'react'
import { Check, Loader2, X } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useSlugAvailability } from '@/features/projects/use-projects'

interface SlugFieldProps {
  /** Current persisted slug — used to short-circuit availability check when draft matches. */
  currentSlug: string
  excludeProjectId: string
  onCommit: (next: string) => void
  disabled?: boolean
}

/**
 * Phase 6: Project slug edit field. Inline format validation + debounced availability check.
 * Commits on Enter or blur when the draft is valid and changed; reverts to the persisted slug on
 * Escape. The persisted slug is the source of truth — the parent receives the new value via
 * {@link SlugFieldProps.onCommit} and is responsible for surfacing 409 conflicts via toast.
 */
export const SlugField = ({
  currentSlug,
  excludeProjectId,
  onCommit,
  disabled,
}: SlugFieldProps) => {
  const [draft, setDraft] = useState(currentSlug)
  useEffect(() => setDraft(currentSlug), [currentSlug])

  const formatProblem = validate(draft)
  const isUnchanged = draft === currentSlug
  // Skip the availability check when the draft equals the current slug (excludeProjectId would
  // already mark it available, but no need to spend the round-trip).
  const availability = useSlugAvailability(
    !isUnchanged && !formatProblem ? draft : undefined,
    excludeProjectId
  )

  const status = isUnchanged
    ? 'unchanged'
    : formatProblem
      ? 'invalid'
      : availability.isPending
        ? 'checking'
        : availability.data?.available
          ? 'available'
          : 'taken'

  const commit = () => {
    if (status !== 'available') return
    onCommit(draft)
  }

  return (
    <div className="space-y-1">
      <Label htmlFor="project-slug" className="text-xs font-medium text-muted-foreground">
        Slug · used in the URL
      </Label>
      <div className="relative">
        <Input
          id="project-slug"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              commit()
            } else if (e.key === 'Escape') {
              setDraft(currentSlug)
            }
          }}
          disabled={disabled}
          className="pr-10 font-mono text-xs"
          aria-describedby="project-slug-status"
        />
        <div className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2">
          {status === 'checking' && (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden />
          )}
          {status === 'available' && <Check className="h-4 w-4 text-emerald-600" aria-hidden />}
          {(status === 'taken' || status === 'invalid') && (
            <X className="h-4 w-4 text-destructive" aria-hidden />
          )}
        </div>
      </div>
      <p id="project-slug-status" className="text-xs text-muted-foreground">
        {status === 'unchanged' && 'Press Enter to save changes.'}
        {status === 'invalid' && (formatProblem ?? 'Invalid format.')}
        {status === 'checking' && 'Checking availability…'}
        {status === 'available' && 'Available — press Enter to save.'}
        {status === 'taken' && reasonCopy(availability.data?.reason)}
      </p>
    </div>
  )
}

const FORMAT_RE = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/

const validate = (slug: string): string | null => {
  if (!slug) return 'Slug is required.'
  if (slug.length < 3) return 'Slug must be at least 3 characters.'
  if (slug.length > 60) return 'Slug must be 60 characters or fewer.'
  if (!FORMAT_RE.test(slug)) {
    return 'Use lowercase letters, numbers, and hyphens (no leading/trailing hyphen).'
  }
  return null
}

const reasonCopy = (reason: 'TAKEN' | 'RESERVED' | 'INVALID' | null | undefined): string => {
  if (reason === 'RESERVED') return 'That slug is reserved — try another.'
  if (reason === 'INVALID') return 'Invalid format.'
  return 'You already have a project with that slug.'
}
